import React, { useState, useRef, useEffect } from "react";
import {
  ChakraProvider,
  Container,
  VStack,
  useToast,
  useDisclosure,
  Box,
  useMediaQuery,
} from "@chakra-ui/react";
import { RegistrationForm } from "./components/RegistrationForm";
import { QRCodeDisplay } from "./components/QRCodeDisplay";
import { PrivacyPolicyModal } from "./components/PrivacyPolicyModal";
import { api } from "./services/api";

function App() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoError, setVideoError] = useState(false);
  const [isIOS] = useState(
    () =>
      /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream
  );
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    jobTitle: "",
    company: "",
    mobilePhone: "",
    email: "",
  });
  const [registrationData, setRegistrationData] = useState<{
    qrCode: string;
    registrationId: string;
    userId: string;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLargerThan768] = useMediaQuery("(min-width: 768px)");
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const [videoReady, setVideoReady] = useState(false);
  const [videoSupported, setVideoSupported] = useState(true);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Check if video format is supported
    const checkVideoSupport = () => {
      const canPlayMP4 = video.canPlayType(
        'video/mp4; codecs="avc1.42E01E, mp4a.40.2"'
      );
      console.log("MP4 support:", canPlayMP4);
      setVideoSupported(!!canPlayMP4);
      return !!canPlayMP4;
    };

    console.log("Video element created, checking support...");
    const isSupported = checkVideoSupport();

    if (!isSupported) {
      console.log("Video format not supported, falling back to static image");
      setVideoError(true);
      return;
    }

    const videoSrc = isLargerThan768
      ? "/background/desktop-optimized.mp4"
      : "/background/mobile-optimized.mp4";
    console.log("Setting video source to:", videoSrc);

    // Try to load the video
    const loadVideo = async () => {
      try {
        // First try to load the video
        video.src = videoSrc;
        await video.load();
        console.log("Video loaded successfully");
        setVideoReady(true);
      } catch (error) {
        console.log("Video load failed:", error);
        setVideoError(true);
      }
    };

    loadVideo();

    const handleError = (e: Event) => {
      console.log("Video error occurred");
      const videoElement = e.target as HTMLVideoElement;
      console.log("Error details:", {
        error: videoElement.error,
        networkState: videoElement.networkState,
        readyState: videoElement.readyState,
        currentSrc: videoElement.currentSrc,
      });
      setVideoError(true);
      setIsVideoLoading(false);
    };

    const handleLoadStart = () => {
      console.log("Video load started");
    };

    const handleLoadedData = () => {
      console.log("Video data loaded");
      setIsVideoLoading(false);
      setVideoReady(true);
    };

    const handleCanPlay = async () => {
      console.log("Video can play");
      try {
        if (isIOS && !hasUserInteracted) {
          console.log("Waiting for user interaction on iOS");
          return;
        }
        await video.play();
        console.log("Video playing successfully");
      } catch (error) {
        console.log("Play failed:", error);
      }
    };

    video.addEventListener("error", handleError);
    video.addEventListener("loadstart", handleLoadStart);
    video.addEventListener("loadeddata", handleLoadedData);
    video.addEventListener("canplay", handleCanPlay);

    // For iOS, handle touch events
    if (isIOS) {
      const handleTouch = async () => {
        console.log("Touch detected, attempting playback");
        setHasUserInteracted(true);
        try {
          await video.play();
          console.log("Video playing after touch");
        } catch (error) {
          console.log("Play failed on touch:", error);
        }
      };
      document.addEventListener("touchstart", handleTouch, { once: true });
      return () => {
        document.removeEventListener("touchstart", handleTouch);
        video.removeEventListener("error", handleError);
        video.removeEventListener("loadstart", handleLoadStart);
        video.removeEventListener("loadeddata", handleLoadedData);
        video.removeEventListener("canplay", handleCanPlay);
      };
    }

    return () => {
      video.removeEventListener("error", handleError);
      video.removeEventListener("loadstart", handleLoadStart);
      video.removeEventListener("loadeddata", handleLoadedData);
      video.removeEventListener("canplay", handleCanPlay);
    };
  }, [isIOS, hasUserInteracted, isLargerThan768]);

  const formatPhoneNumber = (value: string) => {
    // Remove all non-numeric characters
    const numbers = value.replace(/\D/g, "");

    // Format the number as xxx-xxx-xxxx
    if (numbers.length <= 3) {
      return numbers;
    } else if (numbers.length <= 6) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    } else {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(
        6,
        10
      )}`;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "mobilePhone" ? formatPhoneNumber(value) : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await api.register(formData);
      setRegistrationData({
        qrCode: response.data.user.qrCode,
        registrationId: response.data.registrationId,
        userId: response.data.userId,
      });

      console.log(response.data);

      toast({
        title: "Registration Successful",
        description: "Please save the QR code to check-in at the event.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Registration error:", error);
      toast({
        title: "Registration Failed",
        description:
          error instanceof Error
            ? error.message
            : "Unable to complete registration. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegisterAnother = () => {
    setRegistrationData(null);
    setFormData({
      firstname: "",
      lastname: "",
      jobTitle: "",
      company: "",
      mobilePhone: "",
      email: "",
    });
    setPrivacyAccepted(false);
  };

  return (
    <ChakraProvider>
      <Box position="relative" minH="100vh">
        <Box
          position="fixed"
          top="0"
          left="0"
          w="100vw"
          h="100vh"
          bg="#FF3999"
          zIndex="-2"
        />
        {videoError || !videoSupported ? (
          <Box
            position="fixed"
            top="0"
            left="0"
            w="100vw"
            h="100vh"
            bgImage={
              isLargerThan768
                ? "/background/bg-desktop.png"
                : "/background/bg-mobile.png"
            }
            bgSize="cover"
            bgPosition="center"
            zIndex="-1"
          />
        ) : (
          <>
            {/* Loading placeholder */}
            {isVideoLoading && (
              <Box
                position="fixed"
                top="0"
                left="0"
                w="100vw"
                h="100vh"
                bgImage={
                  isLargerThan768
                    ? "/background/bg-desktop.png"
                    : "/background/bg-mobile.png"
                }
                bgSize="cover"
                bgPosition="center"
                zIndex="-1"
              />
            )}
            {/* Video element */}
            <Box
              as="video"
              ref={videoRef}
              position="fixed"
              top="0"
              left="0"
              w="100vw"
              h="100vh"
              objectFit="cover"
              zIndex="-1"
              muted
              playsInline
              preload="auto"
              data-playsinline="true"
              webkit-playsinline="true"
              x5-playsinline="true"
              autoPlay
              opacity={videoReady ? 1 : 0}
              transition="opacity 0.5s ease-in-out"
            />
          </>
        )}

        <Container maxW="container.md" py={10} position="relative">
          <VStack spacing={8}>
            {!registrationData ? (
              <RegistrationForm
                formData={formData}
                isLoading={isLoading}
                privacyAccepted={privacyAccepted}
                onPrivacyChange={setPrivacyAccepted}
                onOpenPrivacy={onOpen}
                onChange={handleChange}
                onSubmit={handleSubmit}
              />
            ) : (
              <QRCodeDisplay
                qrCode={registrationData.qrCode}
                registrationId={registrationData.registrationId}
                userId={registrationData.userId}
                onRegisterAnother={handleRegisterAnother}
              />
            )}
          </VStack>
        </Container>

        <PrivacyPolicyModal isOpen={isOpen} onClose={onClose} />
      </Box>
    </ChakraProvider>
  );
}

export default App;
